import { useEffect, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import Popup from './Popup'
import '../style/components/PhoneVerificationPopup.css'
import { requestPhoneVerification, verifyPhone } from '../api/user'

type PhoneVerificationPopupProps = {
  open: boolean
  cell: string
  onClose: () => void
  onVerified: () => Promise<void> | void
  title?: string
  description?: string
}

const CODE_LENGTH = 4
const FALLBACK_SECONDS = 10 * 60

const formatCountdown = (seconds: number | null) => {
  const total = seconds ?? FALLBACK_SECONDS
  const mins = Math.floor(total / 60)
  const secs = total % 60
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}

function PhoneVerificationPopup({
  open,
  cell,
  onClose,
  onVerified,
  title = 'Verify your phone',
  description = 'Enter the 4-digit code sent to your phone to continue.',
}: PhoneVerificationPopupProps) {
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''))
  const [requesting, setRequesting] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [status, setStatus] = useState<{ type: 'error' | 'success'; message: string } | null>(null)
  const [expiresAt, setExpiresAt] = useState<Date | null>(null)
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null)
  const inputRefs = useRef<Array<HTMLInputElement | null>>([])
  const requestTokenRef = useRef(0)

  useEffect(() => {
    if (!open) return
    setDigits(Array(CODE_LENGTH).fill(''))
    setStatus(null)
    setExpiresAt(null)
    setSecondsLeft(null)
    setVerifying(false)
  }, [open])

  useEffect(() => {
    if (open) {
      inputRefs.current[0]?.focus()
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    if (!cell) {
      setStatus({ type: 'error', message: 'Missing phone number. Please update it and try again.' })
      return
    }
    const requestId = requestTokenRef.current + 1
    requestTokenRef.current = requestId
    setRequesting(true)
    setStatus(null)

    const requestCode = async () => {
      try {
        const data = await requestPhoneVerification(cell)
        if (!data?.sent) {
          throw new Error('Unable to send verification code.')
        }
        const expires = data?.expires_at ? new Date(data.expires_at) : null
        const safeExpires =
          expires && !Number.isNaN(expires.getTime())
            ? expires
            : new Date(Date.now() + FALLBACK_SECONDS * 1000)
        if (requestTokenRef.current === requestId) {
          setExpiresAt(safeExpires)
        }
      } catch (err) {
        if (requestTokenRef.current === requestId) {
          setStatus({
            type: 'error',
            message: err instanceof Error ? err.message : 'Unable to send verification code.',
          })
        }
      } finally {
        if (requestTokenRef.current === requestId) {
          setRequesting(false)
        }
      }
    }

    void requestCode()
  }, [cell, open])

  useEffect(() => {
    if (!open || !expiresAt) {
      setSecondsLeft(null)
      return
    }
    const tick = () => {
      const diffMs = expiresAt.getTime() - Date.now()
      const remaining = Math.max(0, Math.ceil(diffMs / 1000))
      setSecondsLeft(remaining)
    }
    tick()
    const interval = window.setInterval(tick, 1000)
    return () => window.clearInterval(interval)
  }, [expiresAt, open])

  const updateDigit = (index: number, value: string) => {
    const digit = value.slice(-1).replace(/[^0-9]/g, '')
    const next = [...digits]
    next[index] = digit
    setDigits(next)
    setStatus(null)

    if (digit && index < digits.length - 1) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      event.preventDefault()
      if (digits[index]) {
        const next = [...digits]
        next[index] = ''
        setDigits(next)
      } else if (index > 0) {
        const next = [...digits]
        next[index - 1] = ''
        setDigits(next)
        inputRefs.current[index - 1]?.focus()
      }
    }
  }

  const handleVerify = async () => {
    if (requesting || verifying) return
    const code = digits.join('').trim()
    if (!/^[0-9]{4}$/.test(code)) {
      setStatus({ type: 'error', message: 'Enter the 4-digit code.' })
      return
    }
    if (!cell) {
      setStatus({ type: 'error', message: 'Missing phone number. Please update it and try again.' })
      return
    }
    if (secondsLeft !== null && secondsLeft <= 0) {
      setStatus({ type: 'error', message: 'Code expired. Please request a new one.' })
      return
    }

    setVerifying(true)
    setStatus(null)
    try {
      const data = await verifyPhone(cell, code)
      if (!data?.verified) {
        setStatus({ type: 'error', message: 'Incorrect code. Please try again.' })
        return
      }
      await onVerified()
      onClose()
    } catch (err) {
      setStatus({
        type: 'error',
        message: err instanceof Error ? err.message : 'Unable to verify code.',
      })
    } finally {
      setVerifying(false)
    }
  }

  return (
    <Popup
      open={open}
      title={title}
      onClose={onClose}
      showClose={false}
      closeOnBackdrop={false}
    >
      <div className="phone-verify">
        <p className="muted">{requesting ? 'Sending code...' : description}</p>
        <div className="pin-row phone-verify-row">
          {digits.map((digit, pos) => (
            <input
              key={`pin-${pos}`}
              className="pin-input"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => updateDigit(pos, e.target.value)}
              onKeyDown={(e) => handleKeyDown(pos, e)}
              ref={(el) => {
                inputRefs.current[pos] = el
              }}
            />
          ))}
        </div>
        <div className={`phone-verify-timer ${secondsLeft === 0 ? 'expired' : ''}`}>
          Code expires in {formatCountdown(secondsLeft)}
        </div>
        {status && (
          <div className={`phone-verify-status ${status.type}`}>{status.message}</div>
        )}
        <div className="phone-verify-actions">
          <button type="button" className="ghost-button" onClick={onClose} disabled={verifying}>
            Cancel
          </button>
          <button
            type="button"
            className="primary-button"
            onClick={handleVerify}
            disabled={verifying || requesting}
          >
            {verifying ? 'Verifying…' : 'Verify'}
          </button>
        </div>
      </div>
    </Popup>
  )
}

export default PhoneVerificationPopup
